import Image from 'next/image'

interface AvatarProps {
  /** Uploaded avatar or Google picture. Absent, the initial stands in. */
  src?: string | null
  /** Its first letter is the fallback. */
  name?: string | null
  /** Used for the letter when there is no name either. */
  email?: string | null
  /** Width and height, in px. */
  size: number
  /**
   * Accessible name. Omit it where the avatar sits beside the person's name or
   * inside a labelled link, and it is hidden from assistive tech instead.
   */
  label?: string
  /** Border and hover styles — these differ between the nav and the profile page. */
  className?: string
}

/**
 * First letter of the name, else of the email, uppercased. `Array.from` splits
 * by code point, so a name that opens with an emoji or an astral-plane letter
 * does not come out as half a surrogate pair.
 */
export function avatarInitial(name?: string | null, email?: string | null): string {
  const first = Array.from(name?.trim() || email?.trim() || '')[0]
  return first ? first.toLocaleUpperCase() : '?'
}

/**
 * Round profile picture, or the user's initial on a tinted circle when there
 * is no picture. Email + password accounts start without one, so the fallback
 * is the common case for them rather than an edge case.
 */
export function Avatar({ src, name, email, size, label, className = '' }: AvatarProps) {
  return (
    <span
      data-component="Avatar"
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full
        bg-carolina/15 dark:bg-carolina/20 text-carolina-dark dark:text-carolina
        font-semibold leading-none select-none ${className}`}
    >
      {src ? (
        <Image src={src} alt="" width={size} height={size} className="h-full w-full object-cover" />
      ) : (
        avatarInitial(name, email)
      )}
    </span>
  )
}
